import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { LabelWidthValues, MenuPosition } from '../../../types/common/types';
import { isElementOverflowed, isMouseEventOutsideElement, OverflowDirection } from '../../../utils/htmlElement.utils';
import { htmlDecode } from '../../../utils/escape.utils';
import { GzTooltip } from '../../gzTooltip';
import { classMap } from 'lit/directives/class-map.js';
import { DropdownMenuEvents } from '../types';
import IconSet from '../../gzIcon/GzIconSet';

/**
 * @tag gz-dropdown-menu
 * @slot The default slot can be used to insert the dropdown menu content.
 * @slot label-suffix The label-suffix slot can be used to insert additional content on the right side of the label.
 */
@customElement('gz-dropdown-menu')
export class GzDropdownMenuWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  selectedValuesStrings = '';

  @property({ type: String, attribute: true, reflect: true })
  labelWidth = LabelWidthValues.MEDIUM;

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  open = false;

  /**
   * If this property is set, we will display a custom icon from the code base icon pack instead of the default dropdown arrow.
   * The icon string needs to be an id provided from the design system icons pack, i.e 'ds-icon-companies', 'ds-icon-calendar'.
   * See https://rcd-static.npunix.bitdefender.biz/storybooks/nirvana/latest/?path=/story/design-system-all-icons--code-base-icon-pack for the available icons
   */
  @property({ type: String, attribute: true, reflect: true })
  icon = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  @property({ type: String, attribute: true, reflect: true })
  errorTooltipText = '';

  @property({ type: String, attribute: true, reflect: true })
  menuPosition = MenuPosition.LEFT;

  @property({ type: Boolean, attribute: true, reflect: true })
  autoAdjustMenuPosition = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  readonly = false;
  /**
   * This property is used for autoAdjustMenuPosition right offset.
   * You can't take the width of the element inserted in slot, so you can't really know the width of the menu displayed.
   * This is used to control the menu position when there are no space to show the menu.
   * Defaults to 280
   */
  @property({ type: Number, attribute: true, reflect: true })
  offsetRight = 280;

  /**
   * When set to TRUE, this property will display a CLEAR (x) icon button in the label, on the left side of the
   * dropdown icon. When clicked, the button emits a CLEAR event that can be used to clear the field value when this
   * component is used as part of a filter or form field.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  canDelete = false;

  /**
   * When set to TRUE, this property will make the dropdown menu align vertically with respect to the dropdown button.
   * The menu will be aligned above the dropdown button if the button is found in the lower part of the screen (defined
   * by verticalAlignThreshold), otherwise it will stay aligned below the button
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  shouldAlignVertically = false;

  /**
   * Represents the threshold for defining the lower part of the screen as the last X% portion of the screen,
   * defaults to the last 35%. Value should be between 0 and 1.
   */
  @property({ type: Number, attribute: true, reflect: true })
  verticalAlignThreshold = 0.35;

  private errorIcon = 'ds-icon-notification_negative_S';

  @query('#labelValue')
  private labelValue: HTMLElement;

  @query('#secodaryLabel')
  private secondaryLabelElement: HTMLElement;

  @query('#menu')
  private menu: HTMLElement;

  @query('.dropdown')
  private dropdown: HTMLElement;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    const classes = {
      'dropdown-label': true,
      'has-label': this.label,
    };

    return html`
      <div class="dropdown">
        <label class="${classMap(classes)}" part="dropdown-label">
          ${this.renderDropdownLabel()} ${this.renderErrorIcon()} ${this.renderClearButton()}
          <span
            part="dropdown-button"
            class="dropdown-button ${this.icon ? 'custom-icon' : ''}"
            @click="${this.toggleDropdownMenu}"
          >
            ${this.renderDropdownIcon()}
          </span>
        </label>
      </div>
      <div class="dropdown-menu" id="menu">
        <slot></slot>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('mousedown', this.closeIfClickOutside);
    window.addEventListener('click', this.closeIfClickOutside);
  }

  disconnectedCallback() {
    window.removeEventListener('mousedown', this.closeIfClickOutside);
    window.removeEventListener('click', this.closeIfClickOutside);
    super.disconnectedCallback();
  }

  private renderClearButton(): TemplateResult | undefined {
    if (!this.canDelete || this.isError) {
      return;
    }

    if (this.selectedValuesStrings) {
      return html`
        <gz-icon-button
          id="clearButton"
          @click="${() => this.dispatchEvent(new CustomEvent(DropdownMenuEvents.CLEAR))}"
          class="clear-button"
          icon="ds-icon-clear"
          size="24"
          fontsize="20"
          buttontype="square"
          buttonfill="quiet"
          buttoncolor="secondary"
        >
        </gz-icon-button>
      `;
    }
  }

  private renderDropdownIcon() {
    if (this.icon) {
      const iconSetInstance = IconSet.getInstance();
      if (iconSetInstance.isSVGIconAvailable(this.icon)) {
        return html`<gz-icon .name="${this.icon}" size="20" color="secondary"></gz-icon>`;
      } else {
        // Fallback to font icon, in case the icon is not available in the SVG icon set.
        return html`<span class="${this.icon}"></span>`;
      }
    }
    return html`<span class="dropdown-arrow"></span>`;
  }

  private renderErrorIcon(): TemplateResult | undefined {
    if (!this.isError) return;

    return html`
      <gz-tooltip tooltipText="${this.errorTooltipText}" tooltipClass="error">
        <gz-icon
          class="dropdown-button custom-icon"
          color="negative"
          size="24"
          .name="${this.errorIcon}"
          @click="${this.toggleDropdownMenu}"
        ></gz-icon>
      </gz-tooltip>
    `;
  }

  private renderDropdownLabel() {
    const classes = {
      'dropdown-label-main': true,
      'has-value': this.selectedValuesStrings || this.open,
      'has-placeholder': this.placeholder,
    };

    return html`<span
      id="labelValue"
      @mouseenter="${this.handleDropdownMouseEnter}"
      @mousemove="${GzTooltip.align}"
      @mouseleave="${GzTooltip.hide}"
      class="dropdown-label--value"
      @click="${this.toggleDropdownMenu}"
    >
      <span part="main-label" class="${classMap(classes)}">${this.label}</span>
      <span class="dropdown-label--secondary-label" id="secodaryLabel">
        ${this.selectedValuesStrings ? this.selectedValuesStrings : this.renderDropdownPlaceholder()}
      </span>
      <slot name="label-suffix"></slot>
    </span>`;
  }

  private renderDropdownPlaceholder() {
    return html`<span class="dropdown-label--placeholder">${this.placeholder}</span>`;
  }

  private toggleDropdownMenu() {
    if (!this.open) {
      this.openDropdownMenu();
    } else {
      this.closeDropdownMenu();
    }
  }

  public openDropdownMenu() {
    this.alignVertically();
    this.open = true;
    GzTooltip.hide();
    this.dispatchEvent(new CustomEvent(DropdownMenuEvents.OPEN));
  }

  public closeDropdownMenu() {
    this.open = false;
    this.dispatchEvent(new CustomEvent(DropdownMenuEvents.CLOSE));
    this.menu.removeAttribute('style'); // removes properties set when vertically aligned
  }

  private alignVertically(): void {
    if (!this.shouldAlignVertically || !this.shouldAlignAbove()) {
      return;
    }
    this.menu.style.top = 'unset';
    this.menu.style.bottom = 'calc(100% + var(--spacer))';
  }

  private shouldAlignAbove(): boolean {
    const targetRect = this.dropdown.getBoundingClientRect();
    const spaceBelow =
      (window.innerHeight - targetRect.bottom - Math.min(document.body.scrollTop, 0)) / window.innerHeight;
    return spaceBelow < this.verticalAlignThreshold;
  }

  private closeIfClickOutside = (event: MouseEvent) => {
    this.updateMenuPositionInWindow();
    const outsideClick = isMouseEventOutsideElement(event, this);
    if (this.open && outsideClick) {
      this.closeDropdownMenu();
    }
  };

  private handleDropdownMouseEnter(event: MouseEvent) {
    if (this.open || !isElementOverflowed(this.secondaryLabelElement, OverflowDirection.HORIZONTAL)) {
      return;
    }

    // this.label is already encoded. However, html`` encodes again, meaning that we'll encode twice
    // (i.e., &amp; will be re-encoded as &&ampamp;) which will result in faulty display. That's why we need
    // to decode option first.

    const desanitizedSelectedValuesStrings = htmlDecode(this.selectedValuesStrings);

    const tooltipText = `${desanitizedSelectedValuesStrings}`;
    GzTooltip.show(tooltipText, event);
  }

  public updateMenuPositionInWindow() {
    if (!this.autoAdjustMenuPosition) {
      return;
    }
    const windowWidth = window.innerWidth;
    const menuClientRect = this.getBoundingClientRect();
    const offsetRight = this.offsetRight;

    // get available space between left side of the menu and right side of the window
    const availableSpaceRight = windowWidth - menuClientRect.left;
    this.menuPosition = availableSpaceRight < offsetRight ? MenuPosition.RIGHT : MenuPosition.LEFT;
  }

  public centerMenuPositionHorizontally(translateValue: number) {
    this.menu.style.transform = `translateX(${translateValue}px)`;
  }
}
